/**
 * Algolia Autocomplete
 * Autocomplete hooks and configuration for header search
 */
document.addEventListener('algolia.hooks.initialize', function() {
  'use strict';

  const config = window.algoliaThemeConfig || {};
  const utils = window.algoliaUtils || {};
  const templates = window.algoliaTemplates || {};

  /**
   * Configure autocomplete options
   * Sets up mobile behavior, predefined links, panel settings, and analytics tags
   */
  window.algoliaShopify.hooks.registerHook('beforeAutocompleteOptions', (options) => {
    config.log?.('beforeAutocompleteOptions hook called, current options:', options);
    config.log?.('Existing plugins:', options.plugins);

    // Build analytics tags for autocomplete
    const analyticsTags = ['page:autocomplete'];

    // Add device type
    if (config.isMobile && config.isMobile()) {
      analyticsTags.push('device:mobile');
    } else if (window.matchMedia(`(max-width: ${config.breakpoints?.tablet || 1171}px)`).matches) {
      analyticsTags.push('device:tablet');
    } else {
      analyticsTags.push('device:desktop');
    }

    // Add user type
    if (window.Shopify?.customer) {
      analyticsTags.push('user:logged-in');
    } else {
      analyticsTags.push('user:guest');
    }

    // Wrap the search client specifically for autocomplete to inject analytics tags
    // This creates a new wrapper for autocomplete without affecting InstantSearch
    if (options.searchClient && !options.__searchClientWrapped) {
      const originalSearchClient = options.searchClient;

      options.searchClient = {
        ...originalSearchClient,
        search(requests) {
          // Add analytics tags to all autocomplete search requests
          const modifiedRequests = requests.map(request => ({
            ...request,
            params: {
              ...request.params,
              analyticsTags: analyticsTags
            }
          }));

          config.log?.('Autocomplete search with analytics tags:', analyticsTags);

          return originalSearchClient.search(modifiedRequests);
        }
      };

      // Mark as wrapped to prevent double-wrapping
      options.__searchClientWrapped = true;
    }

    config.log?.('Autocomplete analytics tags configured:', analyticsTags);

    /**
     * Helper to check if currently on mobile
     * This is called dynamically, so it works with viewport resizing
     */
    const checkIsMobile = () => {
      return config.isMobile ? config.isMobile() : window.matchMedia('(max-width: 767px)').matches;
    };

    const shouldDisableOnMobile = () => {
      return config.features?.mobileAutocomplete !== true; // Disabled by default
    };

    options.detachedMediaQuery = 'none';
    options.autoFocus = true;
    options.placeholder = config.text?.searchPlaceholder || 'Search...';
    options.panelPlacement = 'full-width';
    options.defaultActiveItemId = 0; // Enable keyboard navigation from start
    options.stallThreshold = 0; // Don't wait for results
    options.initialState = {
      query: '', // Start with empty query to trigger sources on focus
    };

    // Handle Enter key to navigate to search results page
    options.onSubmit = ({ state }) => {
      const query = state.query;
      if (query) {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}search?q=${encodeURIComponent(query)}`;
        window.location.href = url;
      }
    };

    // Dynamic openOnFocus - check mobile state when focus happens
    const originalOpenOnFocus = true;
    Object.defineProperty(options, 'openOnFocus', {
      get: function() {
        const isMobile = checkIsMobile();
        const shouldOpen = isMobile && shouldDisableOnMobile() ? false : originalOpenOnFocus;
        config.log?.('openOnFocus getter called - isMobile:', isMobile, 'shouldOpen:', shouldOpen);
        return shouldOpen;
      }
    });

    // Store autocomplete instance reference
    let autocompleteInstance = null;

    // Add onStateChange to capture instance and handle empty query focus
    const originalOnStateChange = options.onStateChange;
    options.onStateChange = (params) => {
      if (!autocompleteInstance) {
        autocompleteInstance = params.state.context?.autocompleteInstance || params.autocomplete;
      }

      // Call original if exists
      if (originalOnStateChange) {
        originalOnStateChange(params);
      }
    };

    // Dynamic shouldPanelOpen - evaluates on each panel open attempt
    options.shouldPanelOpen = ({ state }) => {
      const isMobile = checkIsMobile();

      config.log?.('shouldPanelOpen called - query:', state.query, 'isMobile:', isMobile, 'collections:', state.collections?.length);

      // Don't open panel on mobile (unless explicitly enabled)
      if (isMobile && shouldDisableOnMobile()) {
        config.log?.('Autocomplete panel blocked - mobile viewport detected');
        return false;
      }

      // On desktop, always allow panel to open (even with empty query)
      // This enables showing predefined suggestions on focus
      return true;
    };

    return options;
  });

  /**
   * Main autocomplete template
   * Controls overall layout with left/right sections
   */
  window.algoliaShopify.hooks.registerHook('beforeAutocompleteMainTemplate',
    function(_defaultTemplate, templateOptions, elements) {
      const { html, state } = templateOptions;
      const query = state.query;

      // Count product hits
      const nbProducts = Number(state?.context?.nbProducts ?? 0);
      const hasProducts = nbProducts > 0;

      config.log?.('Autocomplete - Query:', query, 'Products:', nbProducts);

      return html`
        <div class="aa-PanelLayout aa-Panel--scrollable">
          <div class="aa-PanelSections container">
            <div class="aa-PanelSection--left">
              <div class="aa-SourceHeader">
                <span class="aa-SourceHeaderTitle">${config.text?.popularSearches || 'Popular searches'}</span>
                <div class="aa-SourceHeaderLine" />
              </div>
              ${elements.querySuggestionsPlugin}
              ${elements.collections}
              ${elements.articles}
              ${elements.pages}
              ${elements.redirectUrlPlugin}
            </div>

            <div class="aa-PanelSection--right">
              ${!query || query.trim() === ''
                ? '' // Empty state when no query - just show left section
                : !hasProducts
                  ? html`
                      <div class="aa-PanelLayout aa-Panel--scrollable aa-PanelNoResults">
                        <p class="aa-NoResultsHeader">
                          ${window.algoliaShopify?.translation_helpers?.no_result_for(state.query) || 'No results'}
                        </p>
                        <a class="btn btn--secondary" href="${config.getUrl?.('/collections/bestsellers') || '/collections/bestsellers'}">
                          ${config.text?.shopBestSellers || 'Shop Best Sellers'}
                        </a>
                      </div>
                    `
                  : elements.products}
            </div>
          </div>
        </div>
      `;
    }
  );

  /**
   * Product-only autocomplete template
   * Used when showing only product results
   */
  window.algoliaShopify.hooks.registerHook('beforeAutocompleteMainProductsTemplate',
    function(_defaultTemplate, templateOptions, elements) {
      const { html } = templateOptions;
      return html`
        <div class="aa-PanelLayout aa-Panel--scrollable">
          <div class="aa-PanelSections container">
            <div class="aa-PanelSection--left"></div>
            <div class="aa-PanelSection--right">
              ${elements.products}
            </div>
          </div>
        </div>
      `;
    }
  );

  /**
   * Individual product template in autocomplete
   * Renders each product suggestion
   */
  window.algoliaShopify.hooks.registerHook('beforeAutocompleteProductTemplate',
    function(_defaultTemplate, templateOptions, distinct, itemLink, trackSearchAttribution) {
      const { html, item, components } = templateOptions;

      return html`
        <a
          href="${itemLink}"
          class="aa-ItemLink aa-ProductItem"
          onClick="${() => trackSearchAttribution(item)}"
        >
          <div class="aa-ItemContent">
            <div class="aa-ItemPicture aa-ItemPicture--loaded">
              ${utils.hitIsNew?.(item) ? templates.renderNewBadge?.(html) : null}
              <img
                src="${window.algoliaShopify?.helpers?.renderImage(item, 125).src || ''}"
                srcset="${window.algoliaShopify?.helpers?.renderImage(item, 125).srcset || ''}"
                sizes="${window.algoliaShopify?.helpers?.renderImage(item, 125).sizes || ''}"
                alt="${item.title}"
                loading="lazy"
              />
            </div>
            <div class="aa-ItemContentBody">
              <div class="aa-ItemContentBrand">
                ${item.product_type && components.Highlight({ hit: item, attribute: "product_type" })}
              </div>
              <div class="aa-ItemContentTitleWrapper">
                <div class="aa-ItemContentTitle">
                  ${components.Highlight({ hit: item, attribute: "title" })}
                  ${(() => {
                    const raw = window.algoliaShopify?.helpers?.variantTitleAddition(item, distinct) || "";
                    const variant = utils.formatVariantTitle?.(raw) || "";
                    return variant ? html`<span class="algolia-variant"> - ${variant}</span>` : "";
                  })()}
                </div>
              </div>

              <div class="aa-ItemContentPrice">
                <div class="aa-ItemContentPriceCurrent">
                  ${utils.formatPriceWithPrefix?.(item, distinct) || ''}
                </div>
              </div>
            </div>
          </div>
        </a>
      `;
    }
  );

  /**
   * Section header template
   * Renders headers for different result sections
   */
  window.algoliaShopify.hooks.registerHook('beforeAutocompleteHeaderTemplate',
    function(_defaultTemplate, templateOptions, resource) {
      const { html, state } = templateOptions;

      // Query Suggestions header is rendered in main template, skip it here
      if (resource === 'Query Suggestions' || resource === 'querySuggestionsPlugin') {
        return null;
      }

      // Determine header title based on resource type
      const headerTitle = resource === 'Products'
        ? (config.text?.productSuggestions || 'Product Suggestions')
        : window.algoliaShopify?.translation_helpers?.render_title(resource, state.query) || resource;

      return html`
        <div class="aa-SourceHeader">
          <span class="aa-SourceHeaderTitle">${headerTitle}</span>
          <div class="aa-SourceHeaderLine" />
        </div>
      `;
    }
  );

  /**
   * No results template
   * Shown when search returns no results (and also for empty queries on focus)
   */
  window.algoliaShopify.hooks.registerHook('beforeAutocompleteNoResultsTemplate',
    function(_defaultTemplate, { html, state }, elements) {
      // For empty queries on focus, show predefined suggestions
      if (!state.query || state.query.trim() === '') {
        const PREDEFINED = config.predefinedLinks.map(link => ({
          label: link.label,
          url: config.getUrl(link.url)
        }));

        return html`
          <div class="aa-PanelLayout aa-Panel--scrollable">
            <div class="aa-PanelSections container">
              <div class="aa-PanelSection--left">
                <div class="aa-Source">
                  <div class="aa-SourceHeader">
                    <span class="aa-SourceHeaderTitle">${config.text?.popularSearches || 'Popular searches'}</span>
                    <div class="aa-SourceHeaderLine" />
                  </div>
                  <ul class="aa-List">
                    ${PREDEFINED.map(item => html`
                      <li class="aa-Item">
                        <a class="aa-ItemLink" href="${item.url}">
                          <div class="aa-ItemContent">
                            <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path>
                              </svg>
                            </div>
                            <div class="aa-ItemContentBody">
                              <div class="aa-ItemContentTitle">${item.label}</div>
                            </div>
                          </div>
                        </a>
                      </li>
                    `)}
                  </ul>
                </div>
              </div>
              <div class="aa-PanelSection--right"></div>
            </div>
          </div>
        `;
      }

      return html`
        <div class="aa-PanelLayout aa-Panel--scrollable aa-PanelNoResults">
          <p class="aa-NoResultsHeader">
            ${window.algoliaShopify?.translation_helpers?.no_result_for(state.query) || 'No results'}
          </p>
          <a class="btn btn--secondary" href="${config.getUrl?.('/collections/bestsellers') || '/collections/bestsellers'}">
            ${config.text?.shopBestSellers || 'Shop Best Sellers'}
          </a>
        </div>
      `;
    }
  );

  /**
   * Footer template
   * Shows "View All Products" link with count
   */
  window.algoliaShopify.hooks.registerHook('beforeAutocompleteFooterTemplate',
    function(_defaultTemplate, templateOptions) {
      const { html, state } = templateOptions;
      const root = window.Shopify?.routes?.root || '/';

      return html`
        <div class="aa-footer">
          <a
            class="btn btn--secondary"
            href="${root}search?q=${state.query}"
          >
            ${window.algoliaShopify?.translations?.allProducts || 'View All Products'}
            (${window.algoliaShopify?.helpers?.formatNumber(state.context.nbProducts) || 0})
          </a>
        </div>
      `;
    }
  );

  config.log?.('Algolia Autocomplete hooks registered');

  // Add event listener to track input focus and panel state
  setTimeout(() => {
    const aaInput = document.querySelector('.aa-Input');

    // Handle Enter key on input
    if (aaInput) {
      aaInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const query = aaInput.value;

          if (query && query.trim()) {
            e.preventDefault(); // Prevent default form submission
            e.stopPropagation(); // Stop event from bubbling

            const root = window.Shopify?.routes?.root || '/';
            const url = `${root}search?q=${encodeURIComponent(query.trim())}`;
            window.location.href = url;
          }
        }
      });

      aaInput.addEventListener('focus', () => {
        // Trigger a refresh to force sources to be called
        // This is a workaround because Algolia doesn't call getSources for empty queries on focus
        setTimeout(() => {
          if (aaInput.value === '') {
            // Trigger input event to force Algolia to query sources
            const event = new Event('input', { bubbles: true });
            aaInput.dispatchEvent(event);
          }
        }, 50);
      });
    }
  }, 1000);
});
